<template>
  <v-row justify="center">
    <v-dialog
      v-model="open"
      width="650px"
      :fullscreen="isMobileBreakpoint"
      :persistent="isMobileBreakpoint"
    >
      <v-card dense>
        <v-toolbar elevation="0">
          <v-toolbar-title
            class="deleteVideoModalTitle"
            style="padding-left: 10px"
          >
            <span v-if="removingItems.length === 1">
              {{
                $t('message.pages.video.deleteModal.questionTitle', {
                  count: removingItems.length,
                })
              }}
            </span>
            <span v-else>
              {{
                $t('message.pages.video.deleteModal.questionTitlePlural', {
                  count: removingItems.length,
                })
              }}
            </span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn :disabled="isFetching" icon @click="handleClose()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-card-text class="px-7 py-0">
          <BaseAlert
            class="mb-4"
            type="warning"
            :title="$t('message.pages.folderSettings.alert')"
            :description="
              $t('message.pages.video.deleteModal.confirmRemoveMessage')
            "
          />
          <v-row no-gutters>
            <v-col cols="12">
              {{ $t('message.pages.video.deleteModal.removeItemDesc') }}
            </v-col>

            <v-col cols="12" class="mt-4 labelText mb-1">
              {{ $t('message.writeRemove') }}
            </v-col>

            <v-col cols="12">
              <v-input
                v-model="removeText"
                :rules="[rules.matchesRemoveText]"
                :placeholder="$t('message.pages.video.deleteModal.typeHere')"
                dense
                :error="errors.invalidAccountError"
                outlined
                autofocus
                type="text"
                autocomplete="off"
                aria-autocomplete="off"
              >
                @keyup.enter="handleConfirmDelete" >
              </v-input>
            </v-col>

            <v-col cols="12" class="labelText mb-1">
              {{ $t('message.pages.video.deleteModal.password') }}
            </v-col>

            <v-col cols="12">
              <v-input
                v-model="password"
                class="pb-0"
                :placeholder="
                  $t('message.pages.video.deleteModal.typePassword')
                "
                dense
                outlined
                autocomplete="off"
                :error="errors.invalidAccountError"
                :messages="loginPasswordErrorMessage"
                :append-icon="showPass ? 'mdi-eye' : 'mdi-eye-off'"
                :type="showPass ? 'text' : 'password'"
                @click:append="showPass = !showPass"
              >
                <template v-slot:message="{ message }">
                  <span
                    class="red--text caption align-text"
                    v-if="errors.invalidAccountError"
                  >
                    <v-icon color="red" size="18">mdi-alert-circle</v-icon>
                    {{ message }}
                  </span>
                </template>
              </v-input>
            </v-col>

            <v-col cols="12">
              <v-checkbox
                v-model="checkboxConfirmation"
                class="mt-2"
                hide-details
              >
                <template v-slot:label>
                  <span style="font-size: 14px">{{
                    $t('message.pages.video.deleteUserModal.checkboxText')
                  }}</span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-card-text>

        <v-col cols="12" class="mt-0 pt-0">
          <v-card
            class="d-flex align-center justify-start ml-5"
            width="100%"
            height="20px"
            flat
          >
            <span class="error--text caption" v-if="errors.removeError">
              {{ $t('message.pages.video.deleteModal.errors.removeError') }}
              <br />
              {{ errMsg.removeErrorMsg }}
            </span>
          </v-card>
        </v-col>

        <v-divider></v-divider>
        <v-card-actions
          :class="isMobileBreakpoint ? 'mobile-actions' : ''"
          class="px-7 pb-6 py-6"
        >
          <v-spacer></v-spacer>
          <base-btn
            class="mr-2"
            type="tertiary"
            text
            @click="handleClose"
            :disabled="isFetching"
          >
            {{ $t('message.cancel') }}
          </base-btn>
          <v-tooltip
            v-if="
              isFetching ||
              !removeVerification ||
              !password ||
              !checkboxConfirmation
            "
            top
          >
            <template v-slot:activator="{ on, attrs }">
              <base-btn
                v-bind="attrs"
                v-on="on"
                type="primary"
                :disabled="
                  isFetching ||
                  !removeVerification ||
                  !password ||
                  !checkboxConfirmation
                "
                :loading="isFetching"
                @click="handleConfirmDelete"
              >
                <span>{{ $t('message.confirm') }}</span>
              </base-btn>
            </template>
            <v-container class="text-body-2 pa-0" style="max-width: 230px">
              {{ $t('message.setFieldsCorrectly') }}
            </v-container>
          </v-tooltip>

          <base-btn
            v-else
            type="primary"
            :disabled="
              isFetching ||
              !removeVerification ||
              !password ||
              !checkboxConfirmation
            "
            :loading="isFetching"
            @click="handleConfirmDelete"
          >
            <span>{{ $t('message.confirm') }}</span>
          </base-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import i18n from '@/translations/lang'
import videosApi from '@/services/videosApi'
import foldersApi from '@/services/foldersApi'
import liveService from '@/services/liveService'
import liveServiceV2 from '@/services/liveServiceV2'
import BaseAlert from '@/components/BaseAlert.vue'
import { mapState } from 'vuex'

export default {
  name: 'UnifiedVideoDeleteModal',
  components: {
    BaseAlert,
  },
  data: () => ({
    open: false,
    isFetching: false,
    removingItems: [],
    removeText: '',
    password: '',
    rules: {
      matchesRemoveText: (v) => {
        if (v && v.length > 0) {
          return v.toUpperCase() === i18n.t('message.remove').toUpperCase()
        }
        return true
      },
    },
    showPass: false,
    checkboxConfirmation: false,
    errors: {
      invalidAccountError: false,
      removeError: false,
    },
    deleteError: {
      defaultError: false,
    },
    errMsg: {
      removeErrorMsg: '',
    },
    unknownError: '',
  }),
  computed: {
    ...mapState('user', {
      isMobileBreakpoint: 'isMobileBreakpoint',
    }),
    removeVerification() {
      return (
        this.removeText.toUpperCase() ===
        this.$i18next.t('message.remove').toUpperCase()
      )
    },
    deleteErrorMessage() {
      if (this.deleteError.defaultError) {
        return i18n.t('message.pages.video.deleteModal.errors.defaultError')
      }
      return ''
    },
    loginPasswordErrorMessage() {
      if (this.errors.invalidAccountError) {
        return i18n.t(
          'message.pages.video.deleteModal.errors.invalidAccountError'
        )
      }
      if (this.errors.internalErrorException) {
        return i18n.t('message.pages.video.deleteModal.errors.internalError')
      }
      if (this.errors.unexpectedError) {
        return i18n.t('message.errors.unexpectedError') + this.unknownError
      }
      return ''
    },
  },
  mounted() {
    this.$root.$on('deleteVideoModal', (selectedVideos, type) => {
      Object.assign(this.$data, this.initialState())
      this.removingItems = selectedVideos
      this.type = type
      this.open = true
    })
  },
  beforeDestroy() {
    this.$root.$off('deleteVideoModal')
  },
  methods: {
    ...mapMutations('user', ['UPDATE_GLOBAL_SNACKBAR']),
    ...mapActions('session', ['signInUser']),
    ...mapActions('user', ['removeFolder']),

    async handleConfirmDelete() {
      if (!this.removeVerification || !this.password) return
      this.isFetching = true
      this.errors.invalidAccountError = false

      let confirmResponse = await this.confirmUserPassword()

      if (!confirmResponse) {
        this.isFetching = false
        return
      }

      try {
        let toBeRemovedVideos = []
        let toBeRemovedFolders = []

        for (let item of this.removingItems) {
          if (item.title) toBeRemovedVideos.push(item)
          if (item.name) toBeRemovedFolders.push(item)
        }

        if (toBeRemovedVideos.length > 0) {
          await this.handleDeleteVideos(toBeRemovedVideos)
        }

        if (this.removingItems.length > 0 && this.type === 'Live') {
          await this.handleDeleteLive(this.removingItems)
        }

        if (toBeRemovedFolders.length > 0) {
          await this.deleteFolders(toBeRemovedFolders)
        }

        this.$root.$emit('removedVideoInPage')
        this.$root.$emit('fixUserLimit')
        this.isFetching = false
        this.handleClose()
      } catch (error) {
        this.isFetching = false
        this.errMsg.removeErrorMsg =
          error.response.data.errMsg.message +
          '. ' +
          error.response.data.errCode
        this.errors.removeError = true
      }
    },

    // Confirmação de senha
    async confirmUserPassword() {
      try {
        await this.signInUser({
          username: this.$store.getters['session/userAttributes'].email,
          password: this.password,
        })
        return true
      } catch (error) {
        if (!error.code) {
          const anyError = JSON.parse(JSON.stringify(error))
          if (anyError.message.includes('Network Error')) {
            this.unknownError = 'Code: 500'
            this.errors.invalidAccountError = true
            return false
          }
        }
        if (
          error.code === 'NotAuthorizedException' ||
          error.code === 'InvalidParameterException'
        ) {
          this.errors.invalidAccountError = true
        }
        this.isFetching = false
        return false
      }
    },

    async handleDeleteVideos(videos) {
      let videoIds = videos.map((video) => ({ video_id: video.id }))
      try {
        await videosApi.deleteMultipleVideos(videoIds)
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t('message.deleteSuccess'),
          type: 'success',
          buttons: [
            {
              text: i18n.t('message.goToTrash'),
              size: 'small',
              style: 'font-size: 12px; border: none',
              type: 'secondary',
              onClick: () => {
                this.goToTrash()
              },
            },
          ],
        })
        this.$root.$emit('removeVideoVideosPage', videos)
      } catch (error) {
        console.error(error)
      }
    },

    async handleDeleteLive(lives) {
      try {
        await liveService.cancelLive(lives)
        await liveServiceV2.cancelLive(lives)
        let removedVideoLives = lives.map((live) => ({ id: live.live_id }))
        this.UPDATE_GLOBAL_SNACKBAR({
          open: true,
          text: i18n.t('message.deleteSuccess'),
          type: 'success',
          buttons: [
            {
              text: i18n.t('message.goToTrash'),
              size: 'small',
              style: 'font-size: 12px; border: none',
              type: 'secondary',
              onClick: () => {
                this.goToTrash()
              },
            },
          ],
        })
        this.$root.$emit('removeLivePage', removedVideoLives)
      } catch (error) {
        console.log(error)
      }
    },

    async deleteFolders(folders) {
      for (let folder of folders) {
        try {
          await foldersApi.deleteFolder(folder.id)
          this.removeFolder(folder.id)
          this.UPDATE_GLOBAL_SNACKBAR({
            open: true,
            text: i18n.t('message.deleteSuccess'),
            type: 'success',
            buttons: [
              {
                text: i18n.t('message.goToTrash'),
                size: 'small',
                style: 'font-size: 12px; border: none',
                type: 'secondary',
                onClick: () => {
                  this.goToTrash()
                },
              },
            ],
          })
          this.$root.$emit('removeFolderVideosPage', folder)
        } catch (error) {
          this.isFetching = false
          this.errMsg.removeErrorMsg =
            error.response.data.errMsg.message +
            '. ' +
            error.response.data.errCode
          this.errors.removeError = true
        }
      }
    },

    goToTrash() {
      this.$router.push({ name: 'Trash' })
    },

    // Fechar o modal
    handleClose() {
      this.open = false
      this.$root.$emit('resetIsAnyOtherModal')
    },

    // Resetar o estado inicial
    initialState() {
      return {
        open: false,
        isFetching: false,
        removingItems: [],
        removeText: '',
        password: '',
        showPass: false,
        errors: {
          invalidAccountError: false,
          removeError: false,
        },
        deleteError: {
          defaultError: false,
        },
        errMsg: {
          removeErrorMsg: '',
        },
        unknownError: '',
      }
    },
  },
}
</script>

<style scoped lang="scss">
.deleteVideoModalTitle {
  color: varFigma(neutral-20-dark);
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 120%; /* 21.6px */
}

.labelText {
  color: var(--neutral-40-light, #636578);
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
}

.deleteVideoModalDescription {
  color: varFigma(neutral-40-light);
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
}

.v-snack__wrapper {
  min-width: 700px;
}

.mobile-actions {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: var(--v-background-base);
  z-index: 10;
  padding: 16px;
}
</style>
